import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import CheckIcon from '@material-ui/icons/Check'
import CloseIcon from '@material-ui/icons/Close'
import DeleteIcon from '@material-ui/icons/Delete'
import LinkIcon from '@material-ui/icons/Link'
import LocalShippingIcon from '@material-ui/icons/LocalShipping'
import BlockIcon from '@material-ui/icons/Block'
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore'
import NavigateNextIcon from '@material-ui/icons/NavigateNext'
import axios from 'axios'
import useAggregateVendors from './useAggregateVendors'

// ── Enums (mirroring model) ────────────────────────────────────────────────────

const PRICING_MODES = ['RETAIL', 'WHOLESALE', 'RESELLER', 'PIT_PICKUP', 'YARD_PICKUP', 'DELIVERED', 'UNKNOWN']
const MAPPING_STATUSES = ['AUTO_MAPPED', 'MANUALLY_MAPPED', 'NEEDS_REVIEW', 'UNMATCHED', 'IGNORED']
const UNIT_OPTIONS = ['TON', 'EACH', 'LB', 'SQFT', 'CY', 'UNKNOWN']
const PAGE_SIZE = 25

const COLOR_FAMILIES = ['BROWN', 'GOLD', 'RED', 'GRAY', 'WHITE', 'BLACK', 'TAN', 'PINK', 'GREEN', 'MIXED', 'UNKNOWN']
const MATERIAL_CATEGORIES = ['DECORATIVE_ROCK', 'CONSTRUCTION_AGGREGATE', 'RIP_RAP', 'SAND', 'BOULDER', 'FLAGSTONE', 'SOIL_OR_PLANTING_MIX', 'UNKNOWN']

const CATEGORY_TO_AGGREGATE_TYPES = {
  DECORATIVE_ROCK: ['SCREENED', 'DECO', 'MINUS', 'WASHED'],
  CONSTRUCTION_AGGREGATE: ['SCREENED', 'MINUS', 'ABC', 'WASHED'],
  RIP_RAP: ['RIP_RAP'],
  SAND: ['SAND'],
  BOULDER: ['BOULDER'],
  FLAGSTONE: ['FLAGSTONE'],
  SOIL_OR_PLANTING_MIX: ['SOIL_OR_PLANTING_MIX'],
  UNKNOWN: null
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  filterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    alignItems: 'flex-end'
  },
  filterField: {
    minWidth: 140
  },
  searchField: {
    minWidth: 200
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1.5)
  },
  tableContainer: {
    overflowX: 'auto'
  },
  cellCompact: {
    padding: theme.spacing(0.75, 1.5),
    fontSize: '0.8rem'
  },
  headerCell: {
    padding: theme.spacing(0.75, 1.5),
    fontWeight: 700,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap'
  },
  actionsCell: {
    padding: theme.spacing(0.5, 1),
    whiteSpace: 'nowrap'
  },
  chipRetail: { backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 600, fontSize: '0.68rem', height: 20 },
  chipWholesale: { backgroundColor: '#f3e5f5', color: '#6a1b9a', fontWeight: 600, fontSize: '0.68rem', height: 20 },
  chipPitPickup: { backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.68rem', height: 20 },
  chipYardPickup: { backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.68rem', height: 20 },
  chipDelivered: { backgroundColor: '#fce4ec', color: '#880e4f', fontWeight: 600, fontSize: '0.68rem', height: 20 },
  chipUnknown: { backgroundColor: '#f5f5f5', color: '#757575', fontWeight: 600, fontSize: '0.68rem', height: 20 },
  stockIn: { backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.68rem', height: 20 },
  stockOut: { backgroundColor: '#ffebee', color: '#c62828', fontWeight: 600, fontSize: '0.68rem', height: 20 },
  stockUnknown: { backgroundColor: '#f5f5f5', color: '#9e9e9e', fontWeight: 600, fontSize: '0.68rem', height: 20 },
  mappedIcon: { color: '#2e7d32', fontSize: 18, verticalAlign: 'middle' },
  unmappedIcon: { color: '#bdbdbd', fontSize: 18, verticalAlign: 'middle' },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5)
  },
  paginationText: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: 'center'
  },
  dialogField: {
    marginBottom: theme.spacing(1.5)
  },
  errorText: {
    color: '#c62828',
    fontSize: '0.8rem',
    marginTop: theme.spacing(0.5)
  },
  inlineCreateSection: {
    backgroundColor: '#f5f5f5',
    borderRadius: 6,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(0.5)
  },
  composedTitle: {
    backgroundColor: '#e3f2fd',
    borderRadius: 4,
    padding: theme.spacing(1, 1.5),
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#1565c0'
  },
  optionChip: {
    fontSize: '0.65rem',
    height: 18,
    marginLeft: 8
  },
  showAllToggle: {
    fontSize: '0.72rem',
    textTransform: 'none',
    padding: '0 4px',
    minWidth: 0,
    marginLeft: 8
  },
  quickCalcControls: {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 1fr) minmax(260px, 1.4fr) auto',
    gap: theme.spacing(1.25),
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr'
    }
  },
  quickItemRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 105px 42px',
    gap: theme.spacing(1),
    alignItems: 'center',
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 0
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr'
    }
  },
  quickResultGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
    gap: theme.spacing(1.25),
    marginTop: theme.spacing(2)
  },
  quickResultCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1.25)
  },
  quickResultTotal: {
    fontWeight: 700,
    fontSize: '1.05rem',
    marginTop: theme.spacing(0.5)
  },
  quickDetailRow: {
    padding: theme.spacing(1, 0),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  mutedSmall: {
    fontSize: '0.76rem',
    color: theme.palette.text.secondary
  }
}))

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatPrice (price, unit) {
  if (price == null) return '—'
  return `$${Number(price).toFixed(2)} / ${unit || 'TON'}`
}

function formatMoney (price) {
  if (price == null || Number.isNaN(Number(price))) return '—'
  return `$${Number(price).toFixed(2)}`
}

function formatOfferingLabel (offering) {
  if (!offering) return ''
  const supplier = offering.vendorId && offering.vendorId.name ? offering.vendorId.name : 'Unknown'
  const material = offering.materialProfileId && offering.materialProfileId.displayName
    ? offering.materialProfileId.displayName
    : (offering.productTitle || 'Material')
  const gradation = offering.gradationId && offering.gradationId.displayName
    ? offering.gradationId.displayName
    : (offering.variantTitle || '')
  const price = offering.price != null ? ` - ${formatPrice(offering.price, offering.unit)}` : ''
  return `${supplier} - ${material}${gradation ? ` / ${gradation}` : ''}${price}`
}

function formatJobsiteLabel (jobsite) {
  if (!jobsite) return ''
  const name = jobsite.name || jobsite.address || 'Unnamed jobsite'
  const address = [jobsite.address, jobsite.city, jobsite.state, jobsite.zip].filter(Boolean).join(', ')
  const client = jobsite.client && jobsite.client.companyName ? jobsite.client.companyName : ''
  return [name, client, address].filter(Boolean).join(' - ')
}

function getJobsiteCoords (jobsite) {
  const coords = jobsite && jobsite.location && jobsite.location.coordinates
  if (Array.isArray(coords) && coords.length === 2 && coords[0] != null && coords[1] != null) {
    return { lng: Number(coords[0]), lat: Number(coords[1]) }
  }
  if (jobsite && jobsite.latitude != null && jobsite.longitude != null) {
    return { lat: Number(jobsite.latitude), lng: Number(jobsite.longitude) }
  }
  return null
}

function scenarioLabel (option) {
  if (!option) return ''
  if (option.scenario === 'VENDOR_DELIVERY') return 'Vendor delivery'
  if (option.scenario === 'OUR_TRUCK_20T') return 'Our dump truck'
  if (option.scenario === 'OUR_TRAILER_10T') return 'Our dump trailer'
  return option.scenario || ''
}

function relativeDate (dateStr) {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  const now = Date.now()
  const diffMs = now - d.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 30) return `${diffDays}d ago`
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`
  return `${Math.floor(diffDays / 365)}yr ago`
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function PricingModeChip ({ mode, classes }) {
  const classMap = {
    RETAIL: classes.chipRetail,
    WHOLESALE: classes.chipWholesale,
    PIT_PICKUP: classes.chipPitPickup,
    YARD_PICKUP: classes.chipYardPickup,
    DELIVERED: classes.chipDelivered,
    RESELLER: classes.chipWholesale,
    UNKNOWN: classes.chipUnknown
  }
  return <Chip label={mode} size="small" className={classMap[mode] || classes.chipUnknown} />
}

function AvailabilityChip ({ status, classes }) {
  if (status === 'IN_STOCK') return <Chip label="In Stock" size="small" className={classes.stockIn} />
  if (status === 'OUT_OF_STOCK') return <Chip label="Out of Stock" size="small" className={classes.stockOut} />
  return <Chip label="Unknown" size="small" className={classes.stockUnknown} />
}

// ── Empty offering form ────────────────────────────────────────────────────────

const EMPTY_CREATE_FORM = {
  vendorId: '',
  supplierLocationId: '',
  selectedMaterial: null,
  selectedGradation: null,
  pricingMode: 'UNKNOWN',
  price: '',
  unit: 'TON'
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function OfferingsTab () {
  const classes = useStyles()
  const { suppliers: aggregateVendors } = useAggregateVendors()

  // ── Filter state ──────────────────────────────────────────────────────────────
  const [filterVendor, setFilterVendor] = useState('')
  const [filterLocation, setFilterLocation] = useState('')
  const [filterGradation, setFilterGradation] = useState('')
  const [filterPricingMode, setFilterPricingMode] = useState('')
  const [filterMappingStatus, setFilterMappingStatus] = useState('')
  const [filterSearch, setFilterSearch] = useState('')

  // ── Remote filter options ─────────────────────────────────────────────────────
  const [locations, setLocations] = useState([])
  const [gradations, setGradations] = useState([])

  // ── Table state ───────────────────────────────────────────────────────────────
  const [offerings, setOfferings] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0) // 0-indexed
  const [loading, setLoading] = useState(true)

  // ── Quick calculator state ───────────────────────────────────────────────────
  const [quickCalcOpen, setQuickCalcOpen] = useState(false)
  const [jobsiteInput, setJobsiteInput] = useState('')
  const [jobsiteOptions, setJobsiteOptions] = useState([])
  const [jobsiteLoading, setJobsiteLoading] = useState(false)
  const [selectedJobsite, setSelectedJobsite] = useState(null)
  const jobsiteDebounceRef = useRef(null)
  const [offeringInput, setOfferingInput] = useState('')
  const [offeringOptions, setOfferingOptions] = useState([])
  const [offeringLoading, setOfferingLoading] = useState(false)
  const [selectedQuickOffering, setSelectedQuickOffering] = useState(null)
  const offeringDebounceRef = useRef(null)
  const [quickItems, setQuickItems] = useState([])
  const [quickCalcLoading, setQuickCalcLoading] = useState(false)
  const [quickCalcError, setQuickCalcError] = useState('')
  const [quickCalcResult, setQuickCalcResult] = useState(null)

  // ── Map dialog ────────────────────────────────────────────────────────────────
  const [mapDialogOffering, setMapDialogOffering] = useState(null)
  const [mapCatalogId, setMapCatalogId] = useState('')
  const [mapSaving, setMapSaving] = useState(false)
  const [mapError, setMapError] = useState('')

  // ── Create dialog ─────────────────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM)
  const [createSaving, setCreateSaving] = useState(false)
  const [createError, setCreateError] = useState('')

  // ── Material autocomplete state ───────────────────────────────────────────────
  const [materialInput, setMaterialInput] = useState('')
  const [materialOptions, setMaterialOptions] = useState([])
  const [materialLoading, setMaterialLoading] = useState(false)
  const materialDebounceRef = useRef(null)

  // ── Inline material creation state ────────────────────────────────────────────
  const [showCreateMaterial, setShowCreateMaterial] = useState(false)
  const [newMaterialColor, setNewMaterialColor] = useState('')
  const [newMaterialCategory, setNewMaterialCategory] = useState('')
  const [creatingMaterial, setCreatingMaterial] = useState(false)

  // ── Gradation autocomplete state ──────────────────────────────────────────────
  const [gradationOptions, setGradationOptions] = useState([])
  const [gradationLoading, setGradationLoading] = useState(false)
  const [showAllGradations, setShowAllGradations] = useState(false)

  // ── Load filter options once ──────────────────────────────────────────────────
  useEffect(() => {
    axios.get('/api/aggregate-dashboard/locations?limit=200')
      .then((res) => setLocations(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error('[OfferingsTab] locations fetch error:', err))

    axios.get('/api/aggregate-dashboard/gradations')
      .then((res) => setGradations(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error('[OfferingsTab] gradations fetch error:', err))
  }, [])

  // ── Fetch offerings ───────────────────────────────────────────────────────────
  const fetchOfferings = useCallback(() => {
    setLoading(true)
    const params = {
      limit: PAGE_SIZE,
      skip: page * PAGE_SIZE
    }
    if (filterVendor) params.vendorId = filterVendor
    if (filterLocation) params.locationId = filterLocation
    if (filterGradation) params.gradationId = filterGradation
    if (filterPricingMode) params.pricingMode = filterPricingMode
    if (filterMappingStatus) params.mappingStatus = filterMappingStatus
    if (filterSearch.trim()) params.search = filterSearch.trim()

    axios.get('/api/aggregate-dashboard/offerings', { params })
      .then((res) => {
        // Handle both plain array and { offerings, total } envelope
        if (Array.isArray(res.data)) {
          setOfferings(res.data)
          setTotal(res.data.length < PAGE_SIZE ? page * PAGE_SIZE + res.data.length : (page + 2) * PAGE_SIZE)
        } else {
          setOfferings(res.data.offerings || [])
          setTotal(res.data.total || 0)
        }
      })
      .catch((err) => console.error('[OfferingsTab] fetch error:', err))
      .finally(() => setLoading(false))
  }, [page, filterVendor, filterLocation, filterGradation, filterPricingMode, filterMappingStatus, filterSearch])

  useEffect(() => {
    fetchOfferings()
  }, [fetchOfferings])

  // Reset to page 0 when filters change
  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value)
    setPage(0)
  }

  const handleReset = () => {
    setFilterVendor('')
    setFilterLocation('')
    setFilterGradation('')
    setFilterPricingMode('')
    setFilterMappingStatus('')
    setFilterSearch('')
    setPage(0)
  }

  // ── Pagination ────────────────────────────────────────────────────────────────
  const startRow = page * PAGE_SIZE + 1
  const endRow = page * PAGE_SIZE + offerings.length
  const hasPrev = page > 0
  const hasNext = offerings.length === PAGE_SIZE

  // ── Map dialog handlers ───────────────────────────────────────────────────────
  const handleOpenMapDialog = (offering) => {
    setMapDialogOffering(offering)
    setMapCatalogId(offering.aspireCatalogItemId ? String(offering.aspireCatalogItemId) : '')
    setMapError('')
  }

  const handleCloseMapDialog = () => {
    if (mapSaving) return
    setMapDialogOffering(null)
    setMapCatalogId('')
    setMapError('')
  }

  const handleSubmitMap = async () => {
    if (!mapCatalogId.trim()) { setMapError('Aspire Catalog Item ID is required'); return }
    setMapSaving(true)
    setMapError('')
    try {
      await axios.post(`/api/aggregate-dashboard/offerings/${mapDialogOffering._id}/map`, {
        aspireCatalogItemId: mapCatalogId.trim()
      })
      setMapDialogOffering(null)
      setMapCatalogId('')
      fetchOfferings()
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : err.message
      setMapError(msg)
    } finally {
      setMapSaving(false)
    }
  }

  // ── Ignore handler ────────────────────────────────────────────────────────────
  const handleIgnore = async (offering) => {
    try {
      await axios.post(`/api/aggregate-dashboard/offerings/${offering._id}/ignore`)
      fetchOfferings()
    } catch (err) {
      console.error('[OfferingsTab] ignore error:', err)
    }
  }

  // ── Material autocomplete — debounced search ─────────────────────────────────
  useEffect(() => {
    if (materialDebounceRef.current) clearTimeout(materialDebounceRef.current)

    if (!materialInput || materialInput.length < 2) {
      setMaterialOptions([])
      return
    }

    setMaterialLoading(true)
    materialDebounceRef.current = setTimeout(() => {
      axios.get('/api/aggregate-dashboard/materials', { params: { search: materialInput, limit: 20 } })
        .then((res) => {
          const data = Array.isArray(res.data) ? res.data : []
          setMaterialOptions(data)
        })
        .catch((err) => console.error('[OfferingsTab] material search error:', err))
        .finally(() => setMaterialLoading(false))
    }, 300)

    return () => { if (materialDebounceRef.current) clearTimeout(materialDebounceRef.current) }
  }, [materialInput])

  // ── Gradation options — filtered by material category ────────────────────────
  useEffect(() => {
    if (!createOpen) return
    if (!createForm.selectedMaterial) {
      setGradationOptions([])
      return
    }

    setGradationLoading(true)
    const category = createForm.selectedMaterial.materialCategory
    const allowedTypes = showAllGradations ? null : (category ? CATEGORY_TO_AGGREGATE_TYPES[category] : null)

    if (allowedTypes && allowedTypes.length === 1) {
      // Single type — pass to API directly
      axios.get('/api/aggregate-dashboard/gradations', { params: { aggregateType: allowedTypes[0] } })
        .then((res) => setGradationOptions(Array.isArray(res.data) ? res.data : []))
        .catch((err) => console.error('[OfferingsTab] gradation fetch error:', err))
        .finally(() => setGradationLoading(false))
    } else {
      // Multi-type or show-all: fetch all, filter client-side
      axios.get('/api/aggregate-dashboard/gradations')
        .then((res) => {
          const all = Array.isArray(res.data) ? res.data : []
          if (allowedTypes) {
            setGradationOptions(all.filter((g) => allowedTypes.includes(g.aggregateType)))
          } else {
            setGradationOptions(all)
          }
        })
        .catch((err) => console.error('[OfferingsTab] gradation fetch error:', err))
        .finally(() => setGradationLoading(false))
    }
  }, [createOpen, createForm.selectedMaterial, showAllGradations])

  // ── Inline material creation ──────────────────────────────────────────────────
  const handleCreateMaterial = async () => {
    if (!materialInput || materialInput.length < 2) return
    setCreatingMaterial(true)
    try {
      const res = await axios.post('/api/aggregate-dashboard/materials', {
        displayName: materialInput.trim(),
        colorFamily: newMaterialColor || 'UNKNOWN',
        materialCategory: newMaterialCategory || 'UNKNOWN'
      })
      const newMat = res.data
      setCreateForm((f) => ({ ...f, selectedMaterial: newMat, selectedGradation: null }))
      setMaterialOptions([newMat])
      setShowCreateMaterial(false)
      setNewMaterialColor('')
      setNewMaterialCategory('')
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : err.message
      setCreateError(msg)
    } finally {
      setCreatingMaterial(false)
    }
  }

  // ── Composed product title ────────────────────────────────────────────────────
  const composedProductTitle = createForm.selectedMaterial
    ? createForm.selectedGradation
      ? `${createForm.selectedMaterial.displayName} - ${createForm.selectedGradation.displayName}`
      : createForm.selectedMaterial.displayName
    : ''

  // ── Create dialog handlers ────────────────────────────────────────────────────
  const handleOpenCreate = () => {
    setCreateForm(EMPTY_CREATE_FORM)
    setCreateError('')
    setMaterialInput('')
    setMaterialOptions([])
    setShowCreateMaterial(false)
    setNewMaterialColor('')
    setNewMaterialCategory('')
    setGradationOptions([])
    setShowAllGradations(false)
    setCreateOpen(true)
  }

  const handleCloseCreate = () => {
    if (createSaving) return
    setCreateOpen(false)
    setCreateForm(EMPTY_CREATE_FORM)
    setCreateError('')
    setMaterialInput('')
    setMaterialOptions([])
    setShowCreateMaterial(false)
    setNewMaterialColor('')
    setNewMaterialCategory('')
    setGradationOptions([])
    setShowAllGradations(false)
  }

  const handleCreateChange = (field) => (e) => {
    setCreateForm((f) => ({ ...f, [field]: e.target.value }))
  }

  const handleSubmitCreate = async () => {
    if (!createForm.vendorId) { setCreateError('Vendor is required'); return }
    if (!createForm.selectedMaterial) { setCreateError('Material is required'); return }

    setCreateSaving(true)
    setCreateError('')
    try {
      const payload = {
        vendorId: createForm.vendorId,
        productTitle: composedProductTitle,
        materialProfileId: createForm.selectedMaterial._id,
        pricingMode: createForm.pricingMode || 'UNKNOWN',
        unit: createForm.unit || 'TON',
        sourceType: 'MANUAL_ENTRY'
      }
      if (createForm.supplierLocationId) payload.supplierLocationId = createForm.supplierLocationId
      if (createForm.selectedGradation) {
        payload.gradationId = createForm.selectedGradation._id
        payload.variantTitle = createForm.selectedGradation.displayName
      }
      if (createForm.price !== '') payload.price = parseFloat(createForm.price)

      await axios.post('/api/aggregate-dashboard/offerings', payload)
      setCreateOpen(false)
      setCreateForm(EMPTY_CREATE_FORM)
      setMaterialInput('')
      setMaterialOptions([])
      setShowCreateMaterial(false)
      setGradationOptions([])
      setShowAllGradations(false)
      setPage(0)
      fetchOfferings()
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : err.message
      setCreateError(msg)
    } finally {
      setCreateSaving(false)
    }
  }

  // ── Quick calculator handlers ────────────────────────────────────────────────
  useEffect(() => {
    if (!quickCalcOpen) return undefined
    if (jobsiteDebounceRef.current) clearTimeout(jobsiteDebounceRef.current)

    if (!jobsiteInput || jobsiteInput.length < 2) {
      setJobsiteOptions([])
      return undefined
    }

    setJobsiteLoading(true)
    jobsiteDebounceRef.current = setTimeout(() => {
      axios.get('/api/customer/jobsites/search', { params: { q: jobsiteInput } })
        .then((res) => {
          const data = res.data && Array.isArray(res.data.jobsites) ? res.data.jobsites : []
          setJobsiteOptions(data.filter(site => Boolean(getJobsiteCoords(site))))
        })
        .catch((err) => console.error('[OfferingsTab] jobsite search error:', err))
        .finally(() => setJobsiteLoading(false))
    }, 300)

    return () => { if (jobsiteDebounceRef.current) clearTimeout(jobsiteDebounceRef.current) }
  }, [quickCalcOpen, jobsiteInput])

  useEffect(() => {
    if (!quickCalcOpen) return undefined
    if (offeringDebounceRef.current) clearTimeout(offeringDebounceRef.current)

    if (!offeringInput || offeringInput.length < 2) {
      setOfferingOptions([])
      return undefined
    }

    setOfferingLoading(true)
    offeringDebounceRef.current = setTimeout(() => {
      axios.get('/api/aggregate-dashboard/offerings', {
        params: {
          search: offeringInput.trim(),
          limit: 20
        }
      })
        .then((res) => {
          const data = Array.isArray(res.data) ? res.data : (res.data.offerings || [])
          setOfferingOptions(data.filter(offering => offering.price != null))
        })
        .catch((err) => console.error('[OfferingsTab] offering search error:', err))
        .finally(() => setOfferingLoading(false))
    }, 300)

    return () => { if (offeringDebounceRef.current) clearTimeout(offeringDebounceRef.current) }
  }, [quickCalcOpen, offeringInput])

  const handleOpenQuickCalc = () => {
    setQuickCalcOpen(true)
    setQuickCalcError('')
  }

  const handleCloseQuickCalc = () => {
    if (quickCalcLoading) return
    setQuickCalcOpen(false)
    setQuickCalcError('')
  }

  const handleAddQuickOffering = () => {
    if (!selectedQuickOffering) return
    setQuickItems((items) => {
      if (items.some(item => String(item.offering._id) === String(selectedQuickOffering._id))) return items
      return [...items, { offering: selectedQuickOffering, quantity: 1 }]
    })
    setSelectedQuickOffering(null)
    setOfferingInput('')
    setOfferingOptions([])
    setQuickCalcResult(null)
    setQuickCalcError('')
  }

  const handleQuickQtyChange = (offeringId, value) => {
    setQuickItems((items) => items.map((item) => (
      String(item.offering._id) === String(offeringId)
        ? { ...item, quantity: value }
        : item
    )))
    setQuickCalcResult(null)
  }

  const handleRemoveQuickItem = (offeringId) => {
    setQuickItems((items) => items.filter(item => String(item.offering._id) !== String(offeringId)))
    setQuickCalcResult(null)
  }

  const handleRunQuickCalc = async () => {
    const coords = getJobsiteCoords(selectedJobsite)
    if (!coords) { setQuickCalcError('Select a jobsite with coordinates'); return }
    if (quickItems.length === 0) { setQuickCalcError('Add at least one offering'); return }

    const items = quickItems.map(item => ({
      offeringId: item.offering._id,
      quantity: parseFloat(item.quantity)
    }))
    if (items.some(item => !item.quantity || item.quantity <= 0)) {
      setQuickCalcError('Each offering needs a quantity greater than zero')
      return
    }

    setQuickCalcLoading(true)
    setQuickCalcError('')
    try {
      const res = await axios.post('/api/aggregate-dashboard/fulfillment-calc/quick', {
        jobsiteId: selectedJobsite._id,
        jobsiteLat: coords.lat,
        jobsiteLng: coords.lng,
        items
      })
      setQuickCalcResult(res.data)
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : err.message
      setQuickCalcError(msg)
    } finally {
      setQuickCalcLoading(false)
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────────
  return (
    <div className={classes.root}>
      {/* Filter bar */}
      <div className={classes.filterBar}>
        <TextField
          select
          label="Vendor"
          value={filterVendor}
          onChange={handleFilterChange(setFilterVendor)}
          size="small"
          variant="outlined"
          className={classes.filterField}
        >
          <MenuItem value="">All vendors</MenuItem>
          {aggregateVendors.map((v) => (
            <MenuItem key={v._id} value={v._id}>{v.name}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Location"
          value={filterLocation}
          onChange={handleFilterChange(setFilterLocation)}
          size="small"
          variant="outlined"
          className={classes.filterField}
        >
          <MenuItem value="">All locations</MenuItem>
          {locations.map((loc) => (
            <MenuItem key={loc._id} value={loc._id}>
              {loc.name}{loc.vendorId && loc.vendorId.name ? ` (${loc.vendorId.name})` : ''}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Gradation"
          value={filterGradation}
          onChange={handleFilterChange(setFilterGradation)}
          size="small"
          variant="outlined"
          className={classes.filterField}
        >
          <MenuItem value="">All gradations</MenuItem>
          {gradations.map((g) => (
            <MenuItem key={g._id} value={g._id}>{g.displayName}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Pricing Mode"
          value={filterPricingMode}
          onChange={handleFilterChange(setFilterPricingMode)}
          size="small"
          variant="outlined"
          className={classes.filterField}
        >
          <MenuItem value="">All modes</MenuItem>
          {PRICING_MODES.map((m) => (
            <MenuItem key={m} value={m}>{m}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Mapping Status"
          value={filterMappingStatus}
          onChange={handleFilterChange(setFilterMappingStatus)}
          size="small"
          variant="outlined"
          className={classes.filterField}
        >
          <MenuItem value="">All statuses</MenuItem>
          {MAPPING_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </TextField>

        <TextField
          label="Search"
          value={filterSearch}
          onChange={handleFilterChange(setFilterSearch)}
          size="small"
          variant="outlined"
          className={classes.searchField}
          placeholder="Product title..."
        />

        <Button size="small" onClick={handleReset} variant="text">
          Reset
        </Button>
      </div>

      {/* Table header row */}
      <div className={classes.tableHeader}>
        <Typography variant="subtitle2" color="textSecondary">
          {loading ? 'Loading…' : `${offerings.length === 0 ? 'No' : `${startRow}–${endRow}`} offering${offerings.length !== 1 ? 's' : ''}`}
        </Typography>
        <Box display="flex" style={{ gap: 8 }}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<LocalShippingIcon />}
            onClick={handleOpenQuickCalc}
          >
            Quick Calculator
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
          >
            Add Offering
          </Button>
        </Box>
      </div>

      {/* Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" pt={6}>
          <CircularProgress size={32} />
        </Box>
      ) : offerings.length === 0 ? (
        <Paper variant="outlined">
          <div className={classes.emptyState}>
            <Typography variant="body2" color="textSecondary">
              No offerings found matching the current filters.
            </Typography>
          </div>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" className={classes.tableContainer}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>Supplier</TableCell>
                <TableCell className={classes.headerCell}>Location</TableCell>
                <TableCell className={classes.headerCell}>Material</TableCell>
                <TableCell className={classes.headerCell}>Gradation</TableCell>
                <TableCell className={classes.headerCell}>Mode</TableCell>
                <TableCell className={classes.headerCell}>Price</TableCell>
                <TableCell className={classes.headerCell}>Availability</TableCell>
                <TableCell className={classes.headerCell}>Mapped?</TableCell>
                <TableCell className={classes.headerCell}>Last Seen</TableCell>
                <TableCell className={classes.headerCell}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {offerings.map((o) => (
                <TableRow key={o._id} hover>
                  <TableCell className={classes.cellCompact}>
                    {(o.vendorId && o.vendorId.name) ? o.vendorId.name : '—'}
                  </TableCell>
                  <TableCell className={classes.cellCompact}>
                    {(o.supplierLocationId && o.supplierLocationId.name) ? o.supplierLocationId.name : '—'}
                  </TableCell>
                  <TableCell className={classes.cellCompact}>
                    {(o.materialProfileId && o.materialProfileId.displayName)
                      ? o.materialProfileId.displayName
                      : (o.productTitle || '—')}
                  </TableCell>
                  <TableCell className={classes.cellCompact}>
                    {(o.gradationId && o.gradationId.displayName)
                      ? o.gradationId.displayName
                      : (o.variantTitle || '—')}
                  </TableCell>
                  <TableCell className={classes.cellCompact}>
                    <PricingModeChip mode={o.pricingMode} classes={classes} />
                  </TableCell>
                  <TableCell className={classes.cellCompact} style={{ whiteSpace: 'nowrap' }}>
                    {formatPrice(o.price, o.unit)}
                  </TableCell>
                  <TableCell className={classes.cellCompact}>
                    <AvailabilityChip status={o.availabilityStatus} classes={classes} />
                  </TableCell>
                  <TableCell className={classes.cellCompact} style={{ textAlign: 'center' }}>
                    {o.aspireCatalogItemId
                      ? <CheckIcon className={classes.mappedIcon} titleAccess="Mapped" />
                      : <CloseIcon className={classes.unmappedIcon} titleAccess="Not mapped" />}
                  </TableCell>
                  <TableCell className={classes.cellCompact} style={{ whiteSpace: 'nowrap' }}>
                    {relativeDate(o.lastSeenAt)}
                  </TableCell>
                  <TableCell className={classes.actionsCell}>
                    <Tooltip title="Map to Aspire catalog item">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenMapDialog(o)}
                        style={{ color: o.aspireCatalogItemId ? '#1565c0' : '#666' }}
                      >
                        <LinkIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {o.mappingStatus !== 'IGNORED' && (
                      <Tooltip title="Mark as Ignored">
                        <IconButton
                          size="small"
                          onClick={() => handleIgnore(o)}
                          style={{ color: '#9e9e9e' }}
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      {!loading && offerings.length > 0 && (
        <div className={classes.pagination}>
          <Typography className={classes.paginationText}>
            {startRow}–{endRow}
            {total > 0 && ` of ${total}`}
          </Typography>
          <IconButton size="small" onClick={() => setPage((p) => p - 1)} disabled={!hasPrev}>
            <NavigateBeforeIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => setPage((p) => p + 1)} disabled={!hasNext}>
            <NavigateNextIcon fontSize="small" />
          </IconButton>
        </div>
      )}

      {/* Quick Calculator Dialog */}
      <Dialog open={quickCalcOpen} onClose={handleCloseQuickCalc} maxWidth="md" fullWidth>
        <DialogTitle>Aggregate Quick Calculator</DialogTitle>
        <DialogContent>
          <div className={classes.quickCalcControls}>
            <Autocomplete
              value={selectedJobsite}
              onChange={(e, newValue) => {
                setSelectedJobsite(newValue)
                setQuickCalcResult(null)
                setQuickCalcError('')
              }}
              inputValue={jobsiteInput}
              onInputChange={(e, newInput) => setJobsiteInput(newInput)}
              options={jobsiteOptions}
              loading={jobsiteLoading}
              getOptionLabel={formatJobsiteLabel}
              getOptionSelected={(opt, val) => opt._id === val._id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Jobsite"
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {jobsiteLoading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
              noOptionsText={jobsiteInput.length < 2 ? 'Type to search...' : 'No jobsites with coordinates'}
            />

            <Autocomplete
              value={selectedQuickOffering}
              onChange={(e, newValue) => setSelectedQuickOffering(newValue)}
              inputValue={offeringInput}
              onInputChange={(e, newInput) => setOfferingInput(newInput)}
              options={offeringOptions}
              loading={offeringLoading}
              getOptionLabel={formatOfferingLabel}
              getOptionSelected={(opt, val) => opt._id === val._id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Offering"
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {offeringLoading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
              noOptionsText={offeringInput.length < 2 ? 'Type to search...' : 'No priced offerings'}
            />

            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddQuickOffering}
              disabled={!selectedQuickOffering}
            >
              Add
            </Button>
          </div>

          {quickItems.length > 0 && (
            <Paper variant="outlined">
              {quickItems.map((item) => (
                <div key={item.offering._id} className={classes.quickItemRow}>
                  <Box>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      {formatOfferingLabel(item.offering)}
                    </Typography>
                    <Typography className={classes.mutedSmall}>
                      {(item.offering.supplierLocationId && item.offering.supplierLocationId.name) || 'No source location'}
                    </Typography>
                  </Box>
                  <TextField
                    label="Qty"
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleQuickQtyChange(item.offering._id, e.target.value)}
                    variant="outlined"
                    size="small"
                    inputProps={{ min: 0, step: 0.25 }}
                  />
                  <Tooltip title="Remove">
                    <IconButton size="small" onClick={() => handleRemoveQuickItem(item.offering._id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              ))}
            </Paper>
          )}

          {quickCalcError && (
            <Typography className={classes.errorText}>{quickCalcError}</Typography>
          )}

          {quickCalcResult && Array.isArray(quickCalcResult.summaries) && quickCalcResult.summaries.length > 0 && (
            <>
              <div className={classes.quickResultGrid}>
                {quickCalcResult.summaries.map((summary) => (
                  <div key={summary.category} className={classes.quickResultCard}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="subtitle2">{summary.label}</Typography>
                      {!summary.complete && (
                        <Chip size="small" label={`${summary.missingLineCount} missing`} className={classes.chipUnknown} />
                      )}
                    </Box>
                    <Typography className={classes.quickResultTotal}>
                      {formatMoney(summary.totalCost)}
                    </Typography>
                    <Typography className={classes.mutedSmall}>
                      Materials {formatMoney(summary.materialCost)} + transport {formatMoney(summary.transportCost)}
                    </Typography>
                  </div>
                ))}
              </div>

              <Box mt={2}>
                {quickCalcResult.summaries.map((summary) => (
                  <Box key={`${summary.category}-details`} mb={2}>
                    <Typography variant="subtitle2" style={{ marginBottom: 6 }}>
                      {summary.label} Details
                    </Typography>
                    <Paper variant="outlined">
                      {(summary.items || []).map((option, idx) => (
                        <div key={`${summary.category}-${option.offeringId}-${idx}`} className={classes.quickDetailRow}>
                          <Box px={1.5}>
                            <Box display="flex" justifyContent="space-between" style={{ gap: 12 }}>
                              <Typography variant="body2" style={{ fontWeight: 600 }}>
                                {option.material}{option.gradation ? ` / ${option.gradation}` : ''}
                              </Typography>
                              <Typography variant="body2" style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                {formatMoney(option.totalCost)}
                              </Typography>
                            </Box>
                            <Typography className={classes.mutedSmall}>
                              {option.supplier} - {option.location} - {scenarioLabel(option)} - {option.quantity} ton{Number(option.quantity) === 1 ? '' : 's'} @ {formatMoney(option.pricePerTon)}/ton
                            </Typography>
                            <Typography className={classes.mutedSmall}>
                              {option.trips != null ? `${option.trips} trip${option.trips === 1 ? '' : 's'}` : ''}
                              {option.vehicleCapacity ? `, ${option.vehicleCapacity}t capacity` : ''}
                              {option.routeDistanceMiles ? `, ${option.routeDistanceMiles} mi route` : ''}
                              {option.routeDurationMinutes ? `, ${option.routeDurationMinutes} min` : ''}
                              {option.deliveryZone ? `, zone ${option.deliveryZone}` : ''}
                            </Typography>
                            {option.scenario !== 'VENDOR_DELIVERY' && (
                              <Typography className={classes.mutedSmall}>
                                Material {formatMoney(option.materialCost)}, mileage {formatMoney(option.mileageCost)}, operator {formatMoney(option.operatorCost)}
                              </Typography>
                            )}
                            {option.scenario === 'VENDOR_DELIVERY' && (
                              <Typography className={classes.mutedSmall}>
                                Material {formatMoney(option.materialCost)}, delivery {formatMoney(option.transportCost)}
                                {option.deliveryRatePerLoad ? ` (${formatMoney(option.deliveryRatePerLoad)}/load)` : ''}
                              </Typography>
                            )}
                          </Box>
                          {idx < (summary.items || []).length - 1 && <Divider />}
                        </div>
                      ))}
                    </Paper>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {quickCalcResult && (!quickCalcResult.summaries || quickCalcResult.summaries.length === 0) && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
              No pricing options available for the selected jobsite and offerings.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseQuickCalc} disabled={quickCalcLoading}>Close</Button>
          <Button
            onClick={handleRunQuickCalc}
            color="primary"
            variant="contained"
            disabled={quickCalcLoading || !selectedJobsite || quickItems.length === 0}
          >
            {quickCalcLoading ? <CircularProgress size={18} /> : 'Calculate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map Dialog */}
      <Dialog open={Boolean(mapDialogOffering)} onClose={handleCloseMapDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          Map to Aspire Catalog Item
          {mapDialogOffering && (
            <Typography variant="caption" display="block" color="textSecondary" style={{ marginTop: 2 }}>
              {mapDialogOffering.productTitle}
              {mapDialogOffering.variantTitle ? ` — ${mapDialogOffering.variantTitle}` : ''}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Aspire Catalog Item ID *"
            value={mapCatalogId}
            onChange={(e) => { setMapCatalogId(e.target.value); setMapError('') }}
            variant="outlined"
            size="small"
            fullWidth
            placeholder="MongoDB ObjectId"
            autoFocus
            style={{ marginTop: 8 }}
          />
          {mapError && (
            <Typography className={classes.errorText}>{mapError}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMapDialog} disabled={mapSaving}>Cancel</Button>
          <Button
            onClick={handleSubmitMap}
            color="primary"
            variant="contained"
            disabled={mapSaving}
          >
            {mapSaving ? <CircularProgress size={18} /> : 'Save Mapping'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Offering Dialog */}
      <Dialog open={createOpen} onClose={handleCloseCreate} maxWidth="sm" fullWidth>
        <DialogTitle>Add Offering</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" style={{ gap: 14, paddingTop: 4 }}>
            {/* Vendor */}
            <TextField
              select
              label="Vendor *"
              value={createForm.vendorId}
              onChange={handleCreateChange('vendorId')}
              variant="outlined"
              size="small"
              fullWidth
            >
              {aggregateVendors.length === 0 && (
                <MenuItem value="" disabled>No aggregate vendors configured</MenuItem>
              )}
              {aggregateVendors.map((v) => (
                <MenuItem key={v._id} value={v._id}>{v.name}</MenuItem>
              ))}
            </TextField>

            {/* Location */}
            <TextField
              select
              label="Location"
              value={createForm.supplierLocationId}
              onChange={handleCreateChange('supplierLocationId')}
              variant="outlined"
              size="small"
              fullWidth
            >
              <MenuItem value="">No specific location</MenuItem>
              {locations
                .filter((loc) => !createForm.vendorId || (loc.vendorId && String(loc.vendorId._id || loc.vendorId) === createForm.vendorId))
                .map((loc) => (
                  <MenuItem key={loc._id} value={loc._id}>{loc.name}</MenuItem>
                ))}
            </TextField>

            {/* Material Autocomplete */}
            <Autocomplete
              value={createForm.selectedMaterial}
              onChange={(e, newValue) => {
                if (newValue && newValue._isCreateOption) {
                  setShowCreateMaterial(true)
                  return
                }
                setCreateForm((f) => ({ ...f, selectedMaterial: newValue, selectedGradation: null }))
                setShowCreateMaterial(false)
                setShowAllGradations(false)
              }}
              inputValue={materialInput}
              onInputChange={(e, newInput) => setMaterialInput(newInput)}
              options={materialOptions}
              loading={materialLoading}
              getOptionLabel={(opt) => opt.displayName || ''}
              getOptionSelected={(opt, val) => opt._id === val._id}
              filterOptions={(options) => {
                const result = [...options]
                const trimmed = materialInput.trim()
                if (trimmed.length >= 2) {
                  const exactMatch = options.some((o) => o.displayName && o.displayName.toLowerCase() === trimmed.toLowerCase())
                  if (!exactMatch) {
                    result.push({ _isCreateOption: true, displayName: `+ Create "${trimmed}"` })
                  }
                }
                return result
              }}
              renderOption={(opt) => (
                <Box display="flex" alignItems="center" width="100%">
                  <span>{opt.displayName}</span>
                  {opt.materialCategory && !opt._isCreateOption && (
                    <Chip
                      label={opt.materialCategory}
                      size="small"
                      className={classes.optionChip}
                      style={{ backgroundColor: '#e3f2fd', color: '#1565c0' }}
                    />
                  )}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Material *"
                  variant="outlined"
                  size="small"
                  placeholder="Search materials..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {materialLoading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
              noOptionsText={materialInput.length < 2 ? 'Type to search...' : 'No materials found'}
              fullWidth
            />

            {/* Inline Material Creation */}
            <Collapse in={showCreateMaterial}>
              <div className={classes.inlineCreateSection}>
                <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8, display: 'block' }}>
                  Create new material: <strong>{materialInput}</strong>
                </Typography>
                <Box display="flex" style={{ gap: 10, marginBottom: 10 }}>
                  <Autocomplete
                    value={newMaterialColor || null}
                    onChange={(e, val) => setNewMaterialColor(val || '')}
                    options={COLOR_FAMILIES}
                    renderInput={(params) => (
                      <TextField {...params} label="Color Family" variant="outlined" size="small" />
                    )}
                    size="small"
                    style={{ flex: 1 }}
                    disableClearable={false}
                  />
                  <Autocomplete
                    value={newMaterialCategory || null}
                    onChange={(e, val) => setNewMaterialCategory(val || '')}
                    options={MATERIAL_CATEGORIES}
                    renderInput={(params) => (
                      <TextField {...params} label="Category" variant="outlined" size="small" />
                    )}
                    size="small"
                    style={{ flex: 1 }}
                    disableClearable={false}
                  />
                </Box>
                <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
                  <Button
                    size="small"
                    onClick={() => {
                      setShowCreateMaterial(false)
                      setNewMaterialColor('')
                      setNewMaterialCategory('')
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={handleCreateMaterial}
                    disabled={creatingMaterial}
                  >
                    {creatingMaterial ? <CircularProgress size={16} /> : 'Create'}
                  </Button>
                </Box>
              </div>
            </Collapse>

            {/* Gradation Autocomplete */}
            <Box>
              <Box display="flex" alignItems="center" style={{ marginBottom: 2 }}>
                <Typography variant="caption" color="textSecondary">Gradation / Size</Typography>
                {createForm.selectedMaterial && (
                  <Button
                    size="small"
                    className={classes.showAllToggle}
                    onClick={() => setShowAllGradations((v) => !v)}
                    color={showAllGradations ? 'primary' : 'default'}
                  >
                    {showAllGradations ? 'Filtered' : 'Show All'}
                  </Button>
                )}
              </Box>
              <Autocomplete
                value={createForm.selectedGradation}
                onChange={(e, newValue) => {
                  setCreateForm((f) => ({
                    ...f,
                    selectedGradation: newValue,
                    unit: (newValue && newValue.defaultUnit) ? newValue.defaultUnit : f.unit
                  }))
                }}
                options={gradationOptions}
                loading={gradationLoading}
                disabled={!createForm.selectedMaterial}
                getOptionLabel={(opt) => opt.displayName || ''}
                getOptionSelected={(opt, val) => opt._id === val._id}
                renderOption={(opt) => (
                  <Box display="flex" alignItems="center" width="100%">
                    <span>{opt.displayName}</span>
                    {opt.sizeLabel && (
                      <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                        {opt.sizeLabel}
                      </Typography>
                    )}
                    {opt.aggregateType && (
                      <Chip
                        label={opt.aggregateType}
                        size="small"
                        className={classes.optionChip}
                        style={{ backgroundColor: '#f3e5f5', color: '#6a1b9a' }}
                      />
                    )}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder={createForm.selectedMaterial ? 'Select gradation...' : 'Select a material first'}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {gradationLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
                noOptionsText="No gradations available"
                fullWidth
              />
            </Box>

            {/* Composed Product Title */}
            {composedProductTitle && (
              <div className={classes.composedTitle}>
                {composedProductTitle}
              </div>
            )}

            {/* Pricing Mode */}
            <TextField
              select
              label="Pricing Mode"
              value={createForm.pricingMode}
              onChange={handleCreateChange('pricingMode')}
              variant="outlined"
              size="small"
              fullWidth
            >
              {PRICING_MODES.map((m) => (
                <MenuItem key={m} value={m}>{m}</MenuItem>
              ))}
            </TextField>

            {/* Price + Unit */}
            <Box display="flex" style={{ gap: 10 }}>
              <TextField
                label="Price"
                value={createForm.price}
                onChange={handleCreateChange('price')}
                variant="outlined"
                size="small"
                fullWidth
                type="number"
                placeholder="0.00"
                inputProps={{ min: 0, step: 0.01 }}
              />
              <TextField
                select
                label="Unit"
                value={createForm.unit}
                onChange={handleCreateChange('unit')}
                variant="outlined"
                size="small"
                style={{ minWidth: 90 }}
              >
                {UNIT_OPTIONS.map((u) => (
                  <MenuItem key={u} value={u}>{u}</MenuItem>
                ))}
              </TextField>
            </Box>

            {createError && (
              <Typography className={classes.errorText}>{createError}</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreate} disabled={createSaving}>Cancel</Button>
          <Button
            onClick={handleSubmitCreate}
            color="primary"
            variant="contained"
            disabled={createSaving}
          >
            {createSaving ? <CircularProgress size={18} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
